import { toast } from 'react-toastify';
import encryptJSON from '../ServiceModule/Encrypt';
import { isProdEnv, secretEncryptionKey } from '../ServiceModule/Service';

// Override toast methods to deduplicate identical toast messages (one instance per event) & update active toasts in-place
if (!window._toastMethodsOverridden) {
  window._toastMethodsOverridden = true;
  const toastMethods = ['error', 'success', 'info', 'warn', 'warning', 'dark'];
  toastMethods.forEach((method) => {
    const originalMethod = toast[method];
    if (typeof originalMethod === 'function') {
      toast[method] = (content, options = {}) => {
        if (toast.isActive('no-access-toast')) {
          if (
            content === "You don't have access to this" ||
            (options && options.toastId === 'no-access-toast')
          ) {
            return originalMethod(content, options);
          }
          return null;
        }

        let derivedToastId = options?.toastId;
        if (!derivedToastId && (typeof content === 'string' || typeof content === 'number')) {
          derivedToastId = String(content);
        }

        if (derivedToastId && toast.isActive(derivedToastId)) {
          toast.update(derivedToastId, {
            render: content,
            type: method === 'dark' ? 'default' : method,
            autoClose: options.autoClose || 4000,
            ...options,
          });
          return derivedToastId;
        }

        const finalOptions = derivedToastId ? { toastId: derivedToastId, ...options } : options;
        return originalMethod(content, finalOptions);
      };
    }
  });
}

/**
 * Standardized API response handler for frontend
 * Handles toasts for errors and successes based on standardized backend structure
 */
export const handleApiResponse = (result) => {
  if (result.error) {
    const status = result.error.status;
    const isAbort =
      result.error.name === 'AbortError' ||
      (typeof result.error.error === 'string' &&
        (result.error.error.toLowerCase().includes('abort') ||
          result.error.error.toLowerCase().includes('canceled'))) ||
      (typeof result.error.message === 'string' &&
        (result.error.message.toLowerCase().includes('abort') ||
          result.error.message.toLowerCase().includes('canceled')));

    // Skip toast for aborted/canceled requests
    if (!isAbort) {
      if (Number(status) === 403) {
        const message = result.error.data?.message || '';
        // Only show toast if it's not a CSRF error (which is silently retried in api.js)
        if (!message.toLowerCase().includes('csrf')) {
          toast.error("You don't have access to this", { toastId: 'no-access-toast' });
        }
      } else if (Number(status) !== 401) {
        const message =
          result.error.data?.message ||
          result.error.data?.error ||
          result.error.error ||
          'An unexpected error occurred';
        const lowerMsg = message.toLowerCase();
        const isRateLimit = lowerMsg.includes('please wait');
        const isOtpError =
          (lowerMsg.includes('verification code') ||
            lowerMsg.includes('otp') ||
            lowerMsg.includes('no active') ||
            lowerMsg.includes('invalid code')) &&
          !isRateLimit;

        if (!isOtpError) {
          if (toast.isActive('global-api-toast')) {
            toast.update('global-api-toast', {
              render: message,
              type: 'error',
              autoClose: 8000,
              isLoading: false,
            });
          } else {
            toast.error(message, { toastId: 'global-api-toast', autoClose: 8000 });
          }
        }
      }
    }
    return { success: false, error: result.error };
  }

  if (result.data) {
    // Check for standardized backend success structure (status: true/false)
    if (result.data.status === false) {
      const message = result.data.message || 'Operation failed';
      const lowerMsg = message.toLowerCase();
      const isRateLimit = lowerMsg.includes('please wait');
      const isOtpError =
        (lowerMsg.includes('verification code') ||
          lowerMsg.includes('otp') ||
          lowerMsg.includes('no active') ||
          lowerMsg.includes('invalid code')) &&
        !isRateLimit;

      if (!isOtpError) {
        if (toast.isActive('global-api-toast')) {
          toast.update('global-api-toast', {
            render: message,
            type: 'error',
            autoClose: 8000,
            isLoading: false,
          });
        } else {
          toast.error(message, { toastId: 'global-api-toast', autoClose: 8000 });
        }
      }
      return { success: false, message: result.data.message };
    }
    // Return standard success object
    return {
      success: true,
      data: result.data.data || result.data,
      message: result.data.message,
    };
  }

  return { success: true };
};

/**
 * Cookie helper
 */
export const getCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

/**
 * URL Encoded converter for form data
 */
export const convertToUrlEncoded = (formData) => {
  if (!formData) return '';
  return Object.entries(formData)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
};

/**
 * Payload encryption helper
 * Used specifically for sensitive data in production
 */
export const prepareEncryptedPayload = async (formData) => {
  if (isProdEnv === 'true') {
    const encryptedPayload = await encryptJSON(formData, secretEncryptionKey);
    return JSON.stringify({
      element: encryptedPayload,
    });
  }
  return JSON.stringify(formData);
};
